<script setup>
// Międzyczasy na żywo /live/{alias} (makiety „Wyniki – makiety”: L1 komputer, L2 telefon, L3 rozwinięty
// zawodnik). Nagłówek i stopka jak /{alias} (szeroka kolumna jak /ar), bez zakładek Wyniki/Międzyczasy/
// Statystyki. Komputer: punkty jako kolumny z km, w komórce czas od startu (albo godzina) i miejsce na
// punkcie, ostatni punkt zawodnika w trasie wyróżniony; przy wielu punktach tabela przewija się w bok
// z przyklejonym miejscem i zawodnikiem. Telefon: bez tabeli — wiersz z ostatnim punktem i kropkami
// postępu, dotknięcie = oś punktów. Ulubieni (★) w tej przeglądarce, osobno dla każdego wydarzenia.
//
// Wydajność jak /{alias}: wiersze liczone raz na pobranie (utils/results-list.js + utils/live-splits.js),
// filtry lokalnie, pierwsze wiersze od razu, reszta na raty; co sekundę tylko czas w trasie (RunningTime).
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, toRaw, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useResultsStore } from '../stores/results-fetcher.js'
import EventHeader from './results/EventHeader.vue'
import PartnersFooter from './results/PartnersFooter.vue'
import RunningTime from './results/RunningTime.vue'
import { buildResultsRows, categoryOptions, filterRows } from '../utils/results-list.js'
import { DOTS_MAX, buildLiveColumns, buildLiveRows, lastPointText } from '../utils/live-splits.js'
import { betaEventUrl, eventPhase, formatHourMinute, safePartnerLink } from '../utils/event-info.js'
import { formatClock, parseApiTime } from '../utils/polish-time.js'
import { loadFavorites, saveFavorites } from '../utils/favorites.js'

const REFRESH_MS = 30000
const FIRST_ROWS = 150 // od razu
const ROWS_PER_STEP = 400 // kolejne porcje w tle
const CHANGED_MS = 30000 // jak długo podświetlać zmieniony wiersz
const PHONE_QUERY = '(max-width: 767.98px)'

const resultsStore = useResultsStore()
const route = useRoute()
const alias = String(route.params.eventAlias || '')

const loaded = ref(false)
const lastUpdate = ref(null)
const phase = ref(null)

// Magazyn trzyma poprzednie wydarzenie, dopóki nie przyjdzie nowe — bierzemy tylko to z adresu.
const eventPayload = computed(() => {
  const payload = resultsStore.event
  const ev = payload?.event
  return ev && (!ev.alias || ev.alias === alias) ? payload : null
})
const event = computed(() => eventPayload.value?.event || null)
const sponsors = computed(() => eventPayload.value?.sponsors || [])
const resultsUrl = computed(() => safePartnerLink(event.value?.results_url))

// ---------- wiersze (raz na pobranie) ----------
const columns = shallowRef({ points: [], finish: { key: null, km: '' } })
const rows = shallowRef([])
const hasPoints = ref(false)
const hasGender = ref(false)
const categories = shallowRef([])
const changedIds = shallowRef(new Set())
let previousSignatures = null
let changedTimer = null

function signature(row) {
  return `${row.group}|${row.timeText}|${row.place}|${row.lastIndex}|${row.reachedCount}|${row.penaltyMin}|${row.points}`
}

function markChanged(built) {
  const next = new Map(built.map(row => [row.id, signature(row)]))
  if (previousSignatures) {
    const changed = new Set()
    next.forEach((sig, id) => {
      if (previousSignatures.has(id) && previousSignatures.get(id) !== sig) changed.add(id)
    })
    if (changed.size) {
      changedIds.value = changed
      clearTimeout(changedTimer)
      changedTimer = setTimeout(() => { changedIds.value = new Set() }, CHANGED_MS)
    }
  }
  previousSignatures = next
}

function rebuild() {
  const list = toRaw(resultsStore.results)
  hasPoints.value = resultsStore.shouldShowPointsColumn()
  hasGender.value = resultsStore.hasGender()
  categories.value = toRaw(resultsStore.categories) || []
  const base = buildResultsRows(list, {
    categories: categories.value,
    eventStartMs: parseApiTime(event.value?.date_start),
    gapOf: runner => resultsStore.getGapToLeader(runner, hasPoints.value),
    hasPoints: hasPoints.value
  })
  // magazyn wystawia markery i podbicia jako mapy: marker_id → marker / lista podbić
  const cols = buildLiveColumns(Object.values(toRaw(resultsStore.markersMap) || {}))
  const splits = Object.values(toRaw(resultsStore.splitsMap) || {}).flat()
  const built = buildLiveRows(base, { results: list, columns: cols, splits })
  markChanged(built)
  columns.value = cols
  rows.value = built
  phase.value = eventPhase(event.value, Date.now())
  lastUpdate.value = Date.now()
  scheduleRender()
}

// ---------- ulubieni ----------
const favorites = ref(loadFavorites(alias))
function isFavorite(row) {
  return favorites.value.has(String(row.id))
}
function toggleFavorite(row) {
  const next = new Set(favorites.value)
  const key = String(row.id)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  favorites.value = next
  saveFavorites(alias, next)
}
const favoriteCount = computed(() => rows.value.filter(isFavorite).length)

// ---------- filtry ----------
const query = ref('')
const gender = ref('all')
const category = ref('all')
const timeMode = ref('start') // 'start' = od startu, 'clock' = godzina
const onlyFavorites = ref(false)
const sheetOpen = ref(false)

const catOptions = computed(() => categoryOptions(rows.value, categories.value))
const genderCounts = computed(() => {
  const counts = { all: rows.value.length, K: 0, M: 0 }
  rows.value.forEach(row => { if (row.gender) counts[row.gender] += 1 })
  return counts
})
const baseFiltered = computed(() => filterRows(rows.value, { query: query.value, gender: gender.value, category: category.value }))
const filtered = computed(() => (onlyFavorites.value ? baseFiltered.value.filter(isFavorite) : baseFiltered.value))
// telefon: ulubieni przypięci nad listą (L2)
const pinned = computed(() => (onlyFavorites.value ? [] : baseFiltered.value.filter(isFavorite)))
const filtersActive = computed(() => gender.value !== 'all' || category.value !== 'all' || query.value.trim() !== '' || onlyFavorites.value)
const activeFilterCount = computed(() => (gender.value !== 'all' ? 1 : 0) + (category.value !== 'all' ? 1 : 0))
const categoryLabel = computed(() => catOptions.value.find(c => c.id === category.value)?.name || '')
const hasPointColumns = computed(() => columns.value.points.length > 0)
const hasGpsOnly = computed(() => rows.value.some(row => row.gpsOnly))

function plural(n, one, few, many) {
  if (n === 1) return one
  const last = n % 10
  const lastTwo = n % 100
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? few : many
}

function clearFilters() {
  gender.value = 'all'
  category.value = 'all'
  timeMode.value = 'start'
}

// ---------- telefon / komputer ----------
const isPhone = ref(false)
let phoneMedia = null
function onMediaChange(e) { isPhone.value = e.matches }
function onKeydown(e) {
  if (e.key === 'Escape' && sheetOpen.value) sheetOpen.value = false
}

// ---------- lista do narysowania (na raty) ----------
const items = computed(() => {
  const out = []
  if (isPhone.value) {
    if (pinned.value.length) {
      out.push({ key: 'h-fav', section: `Ulubieni · ${pinned.value.length}` })
      pinned.value.forEach(row => out.push({ key: `f-${row.id}`, row }))
      out.push({ key: 'h-all', section: `Wszyscy · ${filtered.value.length}` })
    } else if (onlyFavorites.value && filtered.value.length) {
      out.push({ key: 'h-fav', section: `Ulubieni · ${filtered.value.length}` })
    }
  }
  filtered.value.forEach(row => out.push({ key: `r-${row.id}`, row }))
  return out
})
const renderLimit = ref(FIRST_ROWS)
const visibleItems = computed(() => (items.value.length > renderLimit.value ? items.value.slice(0, renderLimit.value) : items.value))
let renderHandle = null

function scheduleRender() {
  if (renderHandle || renderLimit.value >= items.value.length) return
  const step = () => {
    renderHandle = null
    renderLimit.value += ROWS_PER_STEP
    scheduleRender()
  }
  renderHandle = window.requestIdleCallback ? window.requestIdleCallback(step, { timeout: 200 }) : setTimeout(step, 16)
}
watch(items, scheduleRender)

// ---------- telefon: rozwinięty zawodnik (L3) ----------
const expandedKey = ref(null)
function toggle(item) {
  expandedKey.value = expandedKey.value === item.key ? null : item.key
}

// ---------- tabela: przewijanie w bok przy wielu punktach (ultra, AR) ----------
// Dopóki tabela mieści się w karcie, karta nie przewija (nagłówek tabeli zostaje przyklejony przy
// przewijaniu strony). Gdy wystaje — przewijanie w bok w karcie, Miejsce, ★ i Zawodnik stoją.
const scrollRef = ref(null)
const tableRef = ref(null)
const tableScrolls = ref(false)
const scrolledX = ref(false)
let scrollObserver = null
function checkTableFits() {
  const box = scrollRef.value
  const table = tableRef.value
  if (!box || !table) return
  tableScrolls.value = table.offsetWidth > box.clientWidth + 1
  // przesunięcia przyklejonych kolumn z rzeczywistych szerokości (tabela ma szerokości automatyczne)
  const head = table.tHead?.rows?.[0]?.cells
  if (head && head.length > 2) {
    table.style.setProperty('--lv-fav-left', `${head[0].offsetWidth}px`)
    table.style.setProperty('--lv-name-left', `${head[0].offsetWidth + head[1].offsetWidth}px`)
  }
}
watch(scrollRef, el => {
  scrollObserver?.disconnect()
  scrollObserver = null
  if (!el || typeof ResizeObserver === 'undefined') return
  scrollObserver = new ResizeObserver(() => checkTableFits())
  scrollObserver.observe(el)
})
watch([columns, visibleItems, timeMode, hasPoints, tableRef], () => nextTick(checkTableFits), { flush: 'post' })
function onTableScroll(e) {
  scrolledX.value = e.target.scrollLeft > 0
}

// ---------- teksty ----------
const nowAtBuild = computed(() => lastUpdate.value || Date.now())

function cellTime(cell) {
  if (timeMode.value === 'clock') return cell.clockText
  return cell.fromText || cell.clockText
}
function cellTitle(col, cell) {
  const parts = [col.label]
  if (cell.fromText) parts.push(`${cell.fromText} od startu`)
  if (cell.clockText) parts.push(`godz. ${cell.clockText}`)
  if (cell.place) parts.push(`${cell.place}. na punkcie`)
  return parts.join(' · ')
}
function metaTime(row) {
  if (!row.meta) return ''
  return timeMode.value === 'clock' ? row.meta.clockText : (row.meta.fromText || row.meta.clockText)
}
function startPending(row) {
  return row.startMs != null && row.startMs > nowAtBuild.value
}
function startText(row) {
  return row.startMs != null ? formatHourMinute(row.startMs) : ''
}
function subLine(row) {
  const parts = [String(row.number ?? '')]
  if (row.categoryName) parts.push(row.categoryPlace ? `${row.categoryName} ${row.categoryPlace}.` : row.categoryName)
  if (row.outOfCompetition) parts.push('poza klasyfikacją')
  if (row.group === 'finished' && row.finishClock) parts.push(`meta ${row.finishClock}`)
  else if (row.group === 'dnf' && row.lastLabel) parts.push(`zszedł po: ${row.lastLabel}`)
  else if (row.group === 'course' && row.lastLabel && phase.value !== 'before') parts.push(lastPointText(row))
  return parts.filter(Boolean).join(' · ')
}
function progressLabel(row) {
  return `Punkty: ${row.reachedCount} z ${row.progress.length}`
}
function dotsStyle(row) {
  return { width: `${Math.min(row.progress.length * 24, 220)}px` }
}
function barStyle(row) {
  return { width: `${Math.round((row.reachedCount / Math.max(row.progress.length, 1)) * 100)}%` }
}

// oś punktów zawodnika (L3): Start, punkty, Meta; „w drodze do …” po ostatnim osiągniętym
function axisFor(row) {
  const points = columns.value.points
  const started = row.reachedCount > 0 || (row.startMs != null && row.startMs <= Date.now())
  const list = [{ key: 'start', label: 'Start', km: '', time: row.startClock, place: '', done: started, current: false, gps: false }]
  points.forEach((col, i) => {
    const cell = row.cells[i]
    list.push({ key: col.key, label: col.label, km: col.km, time: cell.reached ? cellTime(cell) : '', place: cell.place ? `${cell.place}.` : '', done: cell.reached, current: cell.isLast, gps: false })
  })
  list.push({ key: 'meta', label: 'Meta', km: columns.value.finish.km, time: metaTime(row), place: row.place ? `${row.place}.` : '', done: row.group === 'finished', current: false, gps: !!row.meta?.gpsOnly })
  list.forEach((entry, i) => { entry.lineDone = entry.done && !!list[i + 1]?.done })
  if (row.group === 'course' && started && row.lastIndex < 0) list[0].current = true
  const onWayAfter = row.group === 'course' && phase.value === 'live' && started ? row.lastIndex + 1 : -1
  return { list, onWayAfter }
}
// liczona tylko dla rozwiniętego wiersza (zmienia się z przełącznikiem czasu i po pobraniu)
const expandedAxis = computed(() => {
  const item = expandedKey.value ? items.value.find(i => i.key === expandedKey.value) : null
  return item?.row ? axisFor(item.row) : null
})

const mapBase = computed(() => betaEventUrl(alias))
function mapUrl(row) {
  return mapBase.value ? `${mapBase.value}/bib/${encodeURIComponent(row.number)}` : ''
}

// ---------- pobieranie ----------
let refreshTimer = null
onMounted(async () => {
  document.documentElement.classList.add('rv-page')
  phoneMedia = window.matchMedia(PHONE_QUERY)
  isPhone.value = phoneMedia.matches
  phoneMedia.addEventListener?.('change', onMediaChange)
  document.addEventListener('keydown', onKeydown)

  // najpierw wydarzenie: zawodnicy bez start_time liczą czas od startu wydarzenia
  await resultsStore.fetchEventDetails(alias)
  await resultsStore.fetchResults(alias)
  loaded.value = true
  rebuild()
  refreshTimer = setInterval(() => resultsStore.fetchResults(alias), REFRESH_MS)
})

onBeforeUnmount(() => {
  document.documentElement.classList.remove('rv-page')
  phoneMedia?.removeEventListener?.('change', onMediaChange)
  document.removeEventListener('keydown', onKeydown)
  clearInterval(refreshTimer)
  clearTimeout(changedTimer)
  scrollObserver?.disconnect()
  if (renderHandle) (window.cancelIdleCallback || clearTimeout)(renderHandle)
})

// kolejne pobrania (co 30 s) — przelicz wiersze; pierwsze liczy onMounted po wczytaniu wydarzenia
watch(() => resultsStore.results, () => {
  if (loaded.value) rebuild()
})

const updatedAtText = computed(() => (lastUpdate.value ? formatClock(lastUpdate.value) : ''))
</script>

<template>
  <div class="rv">
    <EventHeader :event="event" :alias="alias" :last-update="lastUpdate" wide />

    <main class="rv__main">
      <!-- komputer: filtry w jednym rzędzie (L1) -->
      <div v-if="!isPhone" class="rv-toolbar">
        <label class="rv-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          <input v-model="query" type="search" enterkeyhint="search" placeholder="Szukaj: nazwisko albo numer" aria-label="Szukaj zawodnika po nazwisku albo numerze" />
        </label>
        <div v-if="hasGender" class="rv-seg" role="group" aria-label="Płeć">
          <button v-for="opt in [['all', 'Wszyscy'], ['K', 'Kobiety'], ['M', 'Mężczyźni']]" :key="opt[0]" type="button" :aria-pressed="gender === opt[0] ? 'true' : 'false'" @click="gender = opt[0]">{{ opt[1] }}</button>
        </div>
        <label v-if="catOptions.length > 1" class="rv-select" :class="{ 'is-active': category !== 'all' }">
          <span>Kategoria:</span>
          <select v-model="category" aria-label="Kategoria">
            <option value="all">wszystkie</option>
            <option v-for="c in catOptions" :key="c.id" :value="c.id">{{ c.name }} · {{ c.count }}</option>
          </select>
        </label>
        <span class="rv-toolbar__spacer" />
        <div v-if="hasPointColumns" class="rv-seg" role="group" aria-label="Czasy na punktach">
          <button type="button" :aria-pressed="timeMode === 'start' ? 'true' : 'false'" @click="timeMode = 'start'">Od startu</button>
          <button type="button" :aria-pressed="timeMode === 'clock' ? 'true' : 'false'" @click="timeMode = 'clock'">Godzina</button>
        </div>
        <button type="button" class="lv-favs" :class="{ 'is-active': onlyFavorites }" :aria-pressed="onlyFavorites ? 'true' : 'false'" :title="favoriteCount ? 'Pokaż tylko ulubionych' : 'Oznacz zawodników gwiazdką, żeby mieć ich pod ręką'" @click="onlyFavorites = !onlyFavorites">
          <svg width="18" height="18" viewBox="0 0 24 24" :fill="favoriteCount ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true" class="lv-favs__icon"><path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" /></svg>
          Ulubieni<template v-if="favoriteCount"> · {{ favoriteCount }}</template>
        </button>
      </div>

      <!-- telefon: szukaj + Filtry + ulubieni (L2) -->
      <div v-else class="rv-ptools">
        <div class="rv-ptools__row">
          <label class="rv-search rv-search--phone">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input v-model="query" type="search" enterkeyhint="search" placeholder="Szukaj: nazwisko, nr" aria-label="Szukaj zawodnika po nazwisku albo numerze" />
          </label>
          <button v-if="hasGender || catOptions.length > 1 || hasPointColumns" type="button" class="rv-filters-btn" :class="{ 'is-active': activeFilterCount }" aria-haspopup="dialog" @click="sheetOpen = true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
            Filtry<span v-if="activeFilterCount" class="rv-filters-btn__badge">{{ activeFilterCount }}</span>
          </button>
          <button type="button" class="lv-favs lv-favs--icon" :class="{ 'is-active': onlyFavorites }" :aria-pressed="onlyFavorites ? 'true' : 'false'" aria-label="Tylko ulubieni" @click="onlyFavorites = !onlyFavorites">
            <svg width="19" height="19" viewBox="0 0 24 24" :fill="onlyFavorites ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true" class="lv-favs__icon"><path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" /></svg>
          </button>
        </div>
        <div v-if="activeFilterCount" class="rv-chips">
          <span v-if="gender !== 'all'" class="rv-chip">{{ gender === 'K' ? 'Kobiety' : 'Mężczyźni' }}<button type="button" :aria-label="`Usuń filtr ${gender === 'K' ? 'Kobiety' : 'Mężczyźni'}`" @click="gender = 'all'">×</button></span>
          <span v-if="category !== 'all'" class="rv-chip">{{ categoryLabel }}<button type="button" :aria-label="`Usuń filtr ${categoryLabel}`" @click="category = 'all'">×</button></span>
        </div>
      </div>

      <!-- wstępne czasy z GPS (L1): plakietka „GPS” przy mecie + link do wyników organizatora -->
      <p v-if="hasGpsOnly" class="lv-gpsnote">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
        <span>Czasy oznaczone <strong>GPS</strong> są wstępne – oficjalne pojawią się po weryfikacji na mecie.<template v-if="resultsUrl"> <a :href="resultsUrl" target="_blank" rel="noopener" title="Link prowadzi do zewnętrznej strony">Pełne wyniki organizatora<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg></a></template></span>
      </p>

      <p v-if="filtersActive && loaded && rows.length" class="rv-note">{{ filtered.length }} z {{ rows.length }} · miejsca ogólne</p>

      <div v-if="!loaded" class="rv-empty">Wczytywanie wyników…</div>
      <div v-else-if="!rows.length" class="rv-empty">Brak zawodników na liście.</div>
      <div v-else-if="onlyFavorites && !favoriteCount" class="rv-empty">Brak ulubionych. Dotknij ★ przy zawodniku, żeby mieć go zawsze pod ręką.</div>
      <div v-else-if="!filtered.length" class="rv-empty">Nikt nie pasuje do wyszukiwania.</div>

      <!-- komputer: tabela (L1) -->
      <div v-else-if="!isPhone" class="rv-card" :class="{ 'is-scroll': tableScrolls, 'is-scrolled': scrolledX }">
        <div ref="scrollRef" class="lv-scroll" @scroll.passive="onTableScroll">
          <table ref="tableRef" class="rv-table lv-table">
            <caption class="visually-hidden">Międzyczasy: {{ event?.name || alias }}</caption>
            <thead>
              <tr>
                <th scope="col" class="c-place">Miejsce</th>
                <th scope="col" class="c-fav"><span class="visually-hidden">Ulubiony</span></th>
                <th scope="col" class="c-name">Zawodnik</th>
                <th v-if="catOptions.length" scope="col" class="c-cat">Kat.</th>
                <th v-if="hasPoints" scope="col" class="c-num">Punkty</th>
                <th v-for="col in columns.points" :key="col.key" scope="col" class="c-pt">
                  <span class="lv-th__name">{{ col.label }}</span>
                  <span v-if="col.km" class="lv-th__km">{{ col.km }}</span>
                </th>
                <th scope="col" class="c-pt">
                  <span class="lv-th__name">Meta</span>
                  <span v-if="columns.finish.km" class="lv-th__km">{{ columns.finish.km }}</span>
                </th>
                <th scope="col" class="c-num c-time">Czas</th>
                <th scope="col" class="c-num c-gap">Strata</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in visibleItems" :key="item.key" :class="{ 'is-changed': changedIds.has(item.row.id) }">
                <td class="c-place">
                  <span v-if="item.row.place" class="rv-medal" :class="`rv-medal--${item.row.place}`" :aria-label="`${item.row.place}. miejsce`">{{ item.row.place }}</span>
                  <span v-else-if="item.row.group === 'dnf'" class="rv-tag rv-tag--dnf">DNF</span>
                  <span v-else-if="item.row.outOfCompetition" class="rv-tag" title="Poza klasyfikacją">poza kl.</span>
                  <span v-else class="rv-dash">–</span>
                </td>
                <td class="c-fav">
                  <button type="button" class="lv-star" :class="{ 'is-on': isFavorite(item.row) }" :aria-pressed="isFavorite(item.row) ? 'true' : 'false'" :aria-label="`${item.row.name} – ulubiony`" @click="toggleFavorite(item.row)">
                    <svg width="18" height="18" viewBox="0 0 24 24" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" /></svg>
                  </button>
                </td>
                <td class="c-name">
                  <span class="rv-name"><span class="rv-bib">{{ item.row.number }}</span>{{ item.row.name }}</span>
                  <span v-if="item.row.bio" class="rv-bio">{{ item.row.bio }}</span>
                </td>
                <td v-if="catOptions.length" class="c-cat">{{ item.row.categoryName }}<strong v-if="item.row.categoryPlace">{{ ' ' + item.row.categoryPlace + '.' }}</strong></td>
                <td v-if="hasPoints" class="c-num"><strong>{{ item.row.points }}</strong></td>
                <td v-for="(col, i) in columns.points" :key="col.key" class="c-pt">
                  <span v-if="item.row.cells[i].reached" class="lv-cell" :class="{ 'is-last': item.row.cells[i].isLast }" :title="cellTitle(col, item.row.cells[i])">
                    <span class="lv-cell__time">{{ cellTime(item.row.cells[i]) }}</span><span v-if="item.row.cells[i].place" class="lv-cell__place">{{ item.row.cells[i].place }}.</span>
                  </span>
                  <span v-else class="lv-none" :aria-label="`${col.label}: jeszcze nie`">·</span>
                </td>
                <td class="c-pt">
                  <span v-if="item.row.meta" class="lv-cell lv-cell--meta">
                    <span class="lv-cell__time">{{ metaTime(item.row) }}</span><span v-if="item.row.meta.gpsOnly" class="rv-gps" title="Czas wstępny z GPS">GPS</span>
                  </span>
                  <span v-else class="lv-none" aria-label="Meta: jeszcze nie">·</span>
                </td>
                <td class="c-num c-time">
                  <template v-if="item.row.group === 'finished'">
                    <span class="rv-time">{{ item.row.timeText }}</span>
                    <span v-if="item.row.penaltyMin" class="rv-penalty" :title="item.row.penaltyComment || undefined">w tym kara +{{ item.row.penaltyMin }} min</span>
                  </template>
                  <template v-else-if="item.row.group === 'course'">
                    <span v-if="phase === 'ended'" class="rv-muted">brak mety</span>
                    <span v-else-if="startPending(item.row) || phase === 'before'" class="rv-muted">{{ startText(item.row) ? `start ${startText(item.row)}` : '' }}</span>
                    <RunningTime v-else-if="phase === 'live'" class="lv-running" :start-ms="item.row.startMs" />
                  </template>
                </td>
                <td class="c-num c-gap">{{ item.row.gapText }}<span v-if="item.row.pointsGapText" class="rv-sub">{{ item.row.pointsGapText }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="visibleItems.length < items.length" class="rv-more">Wczytywanie kolejnych wierszy…</p>
      </div>

      <!-- telefon: lista (L2), dotknięcie = oś punktów (L3) -->
      <ol v-else class="rv-plist" aria-label="Międzyczasy">
        <template v-for="item in visibleItems" :key="item.key">
          <li v-if="item.section" class="rv-plist__section" role="presentation"><h2>{{ item.section }}</h2></li>
          <li v-else class="lv-pitem" :class="{ 'is-changed': changedIds.has(item.row.id), 'is-open': expandedKey === item.key }">
            <div class="lv-prow">
              <button type="button" class="lv-prow__btn" :aria-expanded="expandedKey === item.key ? 'true' : 'false'" :aria-controls="`lv-axis-${item.key}`" @click="toggle(item)">
                <span class="rv-prow__place">
                  <span v-if="item.row.place" class="rv-medal" :class="`rv-medal--${item.row.place}`" :aria-label="`${item.row.place}. miejsce`">{{ item.row.place }}</span>
                  <span v-else-if="item.row.group === 'dnf'" class="rv-tag rv-tag--dnf">DNF</span>
                  <span v-else class="rv-dash">–</span>
                </span>
                <span class="rv-prow__main">
                  <span class="rv-prow__name">{{ item.row.name }}</span>
                  <span class="rv-prow__sub">{{ subLine(item.row) }}<template v-if="item.row.gpsOnly"> <span class="rv-gps">GPS</span></template></span>
                  <span v-if="hasPointColumns && item.row.progress.length <= DOTS_MAX" class="lv-dots" :style="dotsStyle(item.row)" role="img" :aria-label="progressLabel(item.row)">
                    <template v-for="(done, i) in item.row.progress" :key="i">
                      <span v-if="i" class="lv-dots__line" :class="{ 'is-done': done }" aria-hidden="true" />
                      <span class="lv-dots__dot" :class="{ 'is-done': done }" aria-hidden="true" />
                    </template>
                  </span>
                  <span v-else-if="hasPointColumns" class="lv-bar" role="img" :aria-label="progressLabel(item.row)">
                    <span class="lv-bar__track" aria-hidden="true"><span class="lv-bar__fill" :style="barStyle(item.row)" /></span>
                    <span class="lv-bar__text" aria-hidden="true">{{ item.row.reachedCount }}/{{ item.row.progress.length }}</span>
                  </span>
                </span>
                <span class="rv-prow__right">
                  <template v-if="item.row.group === 'finished'">
                    <span class="rv-time">{{ hasPoints ? `${item.row.points} pkt` : item.row.timeText }}</span>
                    <span class="rv-sub">{{ hasPoints ? item.row.timeText : (item.row.gapText || '\u00a0') }}</span>
                    <span v-if="item.row.penaltyMin" class="rv-penalty">kara +{{ item.row.penaltyMin }} min</span>
                  </template>
                  <template v-else-if="item.row.group === 'course'">
                    <span v-if="phase === 'ended'" class="rv-muted">brak mety</span>
                    <span v-else-if="startPending(item.row) || phase === 'before'" class="rv-muted">{{ startText(item.row) ? `start ${startText(item.row)}` : '' }}</span>
                    <template v-else-if="phase === 'live'"><RunningTime class="lv-running rv-time" :start-ms="item.row.startMs" /><span class="rv-sub">w trasie</span></template>
                  </template>
                </span>
              </button>
              <button type="button" class="lv-star lv-star--phone" :class="{ 'is-on': isFavorite(item.row) }" :aria-pressed="isFavorite(item.row) ? 'true' : 'false'" :aria-label="`${item.row.name} – ulubiony`" @click="toggleFavorite(item.row)">
                <svg width="19" height="19" viewBox="0 0 24 24" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" /></svg>
              </button>
            </div>

            <div v-if="expandedKey === item.key && expandedAxis" :id="`lv-axis-${item.key}`" class="lv-axis">
              <ol class="lv-axis__list">
                <template v-for="(entry, i) in expandedAxis.list" :key="entry.key">
                  <li class="lv-axis__item" :class="{ 'is-done': entry.done, 'is-current': entry.current, 'is-line-done': entry.lineDone, 'is-end': entry.key === 'meta' }">
                    <span class="lv-axis__node" aria-hidden="true" />
                    <span class="lv-axis__label">{{ entry.label }}<span v-if="entry.km" class="lv-axis__km"> · {{ entry.km }}</span></span>
                    <span class="lv-axis__value"><strong>{{ entry.time }}</strong><span v-if="entry.place" class="lv-axis__place">{{ ' ' + entry.place }}</span><span v-if="entry.gps" class="rv-gps">GPS</span></span>
                  </li>
                  <li v-if="i === expandedAxis.onWayAfter" class="lv-axis__onway">
                    w drodze do {{ item.row.nextLabel }} · od startu <RunningTime :start-ms="item.row.startMs" />
                  </li>
                </template>
              </ol>
              <div class="lv-axis__actions">
                <a v-if="mapUrl(item.row)" class="lv-btn lv-btn--primary" :href="mapUrl(item.row)" target="_blank" rel="noopener">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z" /><path d="M9 4v14M15 6v14" /></svg>
                  Na mapie
                </a>
              </div>
            </div>
          </li>
        </template>
        <li v-if="visibleItems.length < items.length" class="rv-more" role="presentation">Wczytywanie kolejnych wierszy…</li>
      </ol>
    </main>

    <PartnersFooter :sponsors="sponsors" :updated-at="phase === 'live' ? updatedAtText : ''" wide />

    <!-- telefon: arkusz „Filtry” -->
    <div v-if="sheetOpen" class="rv-sheet" @click.self="sheetOpen = false">
      <div class="rv-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="lv-sheet-title">
        <div class="rv-sheet__head">
          <h2 id="lv-sheet-title">Filtry</h2>
          <button type="button" class="rv-link" @click="clearFilters">Wyczyść</button>
        </div>
        <template v-if="hasGender">
          <h3>Płeć</h3>
          <div class="rv-seg rv-seg--wide" role="group" aria-label="Płeć">
            <button v-for="opt in [['all', 'Wszyscy'], ['K', 'Kobiety'], ['M', 'Mężczyźni']]" :key="opt[0]" type="button" :aria-pressed="gender === opt[0] ? 'true' : 'false'" @click="gender = opt[0]">{{ opt[1] }} · {{ genderCounts[opt[0]] }}</button>
          </div>
        </template>
        <template v-if="catOptions.length > 1">
          <h3>Kategoria</h3>
          <div class="rv-sheet__chips">
            <button type="button" class="rv-pick" :aria-pressed="category === 'all' ? 'true' : 'false'" @click="category = 'all'">Wszystkie</button>
            <button v-for="c in catOptions" :key="c.id" type="button" class="rv-pick" :aria-pressed="category === c.id ? 'true' : 'false'" @click="category = c.id">{{ c.name }} · {{ c.count }}</button>
          </div>
        </template>
        <template v-if="hasPointColumns">
          <h3>Czasy na punktach</h3>
          <div class="rv-seg rv-seg--wide" role="group" aria-label="Czasy na punktach">
            <button type="button" :aria-pressed="timeMode === 'start' ? 'true' : 'false'" @click="timeMode = 'start'">Od startu</button>
            <button type="button" :aria-pressed="timeMode === 'clock' ? 'true' : 'false'" @click="timeMode = 'clock'">Godzina</button>
          </div>
        </template>
        <button type="button" class="rv-sheet__go" @click="sheetOpen = false">Pokaż {{ filtered.length }} {{ plural(filtered.length, 'osobę', 'osoby', 'osób') }}</button>
      </div>
    </div>
  </div>
</template>

<style>
/* App.vue ustawia #app na wyśrodkowany tekst z marginesem — ta strona ma własny układ (jak /{alias}) */
html.rv-page #app {
  margin-top: 0;
  text-align: left;
  color: var(--sledz-text);
}
html.rv-page body {
  background: var(--sledz-surface-alt);
}
</style>

<style scoped>
/* Kolory tylko z palety --sledz-*; jaśniejsze odcienie przez color-mix z zapasem z palety
   w deklaracji wyżej (przeglądarki bez color-mix). */
.rv {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--sledz-surface-alt);
  color: var(--sledz-text);
}
.rv__main {
  flex: 1;
  width: 100%;
  max-width: 1376px;
  margin: 0 auto;
  padding: 20px 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ---------- toolbar ---------- */
.rv-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.rv-toolbar__spacer {
  flex: 1;
}
.rv-search {
  flex: none;
  width: 300px;
  height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid var(--sledz-border);
  border-radius: 10px;
  background: var(--sledz-surface);
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--sledz-text-muted);
}
.rv-search:focus-within {
  border-color: var(--sledz-primary);
  box-shadow: 0 0 0 3px var(--sledz-primary-soft);
}
.rv-search input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  color: var(--sledz-text);
}
.rv-seg {
  display: inline-flex;
  padding: 3px;
  border-radius: 20px;
  background: var(--sledz-border-soft);
}
.rv-seg button {
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 17px;
  background: transparent;
  color: var(--sledz-text-secondary);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.rv-seg button[aria-pressed='true'] {
  background: var(--sledz-surface);
  color: var(--sledz-text);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
.rv-select {
  height: 40px;
  margin: 0;
  padding: 0 4px 0 12px;
  border: 1px solid var(--sledz-border);
  border-radius: 10px;
  background: var(--sledz-surface);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: var(--sledz-text-muted);
}
.rv-select.is-active {
  border: 1.5px solid var(--sledz-primary);
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.rv-select select {
  height: 36px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  color: var(--sledz-text);
  cursor: pointer;
}
.lv-favs {
  height: 40px;
  padding: 0 14px;
  border: 1px solid var(--sledz-border);
  border-radius: 10px;
  background: var(--sledz-surface);
  color: var(--sledz-text);
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.lv-favs__icon {
  color: var(--sledz-warning-ink);
}
.lv-favs.is-active {
  border: 1.5px solid var(--sledz-primary);
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.lv-favs--icon {
  flex: none;
  width: 44px;
  height: 44px;
  padding: 0;
  justify-content: center;
}
.rv-note {
  margin: 0;
  font-size: 13.5px;
  color: var(--sledz-text-muted);
}
.rv-empty {
  padding: 40px 16px;
  text-align: center;
  color: var(--sledz-text-muted);
}

/* ---------- GPS ---------- */
.lv-gpsnote {
  margin: 0;
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--sledz-warning-soft);
  border: 1px solid var(--sledz-warning-soft);
  border-color: color-mix(in srgb, var(--sledz-warning) 35%, var(--sledz-surface));
  color: var(--sledz-warning-ink);
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  line-height: 1.45;
}
.lv-gpsnote > svg {
  flex: none;
  margin-top: 2px;
}
.lv-gpsnote a {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  font-weight: 800;
  color: var(--sledz-primary-deep);
  text-decoration: none;
  white-space: nowrap;
}
.lv-gpsnote a:hover {
  text-decoration: underline;
}

/* ---------- desktop table ---------- */
.rv-card {
  background: var(--sledz-surface);
  border: 1px solid var(--sledz-border-soft);
  border-radius: 14px;
  overflow: hidden;
  overflow: clip; /* nie tworzy kontenera przewijania — nagłówek tabeli zostaje przyklejony */
}
.rv-card.is-scroll .lv-scroll {
  overflow-x: auto;
  overflow-y: hidden; /* samo overflow-x: auto dawało w /ar drugi pionowy pasek */
}
.rv-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.rv-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 52px;
  padding: 6px 12px;
  background: var(--sledz-surface-alt);
  border-bottom: 1px solid var(--sledz-border-soft);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
  text-align: left;
  white-space: nowrap;
  vertical-align: bottom;
}
.rv-table td {
  height: 50px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--sledz-border-soft);
  background: var(--sledz-surface);
  vertical-align: middle;
  font-size: 14px;
}
.rv-table tbody tr:last-child td {
  border-bottom: none;
}
.rv-table tbody tr.is-changed td {
  animation: rv-flash 1.2s ease-out;
  background: var(--sledz-highlight);
}
@keyframes rv-flash {
  0% { background: var(--sledz-highlight-flash); }
  100% { background: var(--sledz-highlight); }
}
.rv-table .c-place {
  width: 76px;
  min-width: 76px;
  padding-left: 8px;
  padding-right: 8px;
  text-align: center;
}
.rv-table .c-fav {
  width: 40px;
  min-width: 40px;
  padding: 0 2px;
  text-align: center;
}
.rv-table .c-name {
  min-width: 200px;
}
.rv-table .c-cat {
  width: 90px;
  white-space: nowrap;
  color: var(--sledz-text-secondary);
}
.rv-table .c-num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.rv-table .c-time {
  width: 110px;
}
.rv-table .c-gap {
  width: 84px;
  color: var(--sledz-text-muted);
}
.rv-table .c-pt {
  min-width: 104px;
  text-align: center;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.rv-table thead .c-pt {
  white-space: normal;
}
.lv-th__name {
  display: block;
  max-width: 150px;
  margin: 0 auto;
  line-height: 1.25;
  /* łamanie tylko między słowami — kolumna rośnie do najdłuższego słowa */
  overflow-wrap: normal;
}
.lv-th__km {
  display: block;
  margin-top: 1px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: var(--sledz-text-muted);
}

/* przyklejone kolumny przy przewijaniu w bok */
.rv-card.is-scroll .c-place,
.rv-card.is-scroll .c-fav,
.rv-card.is-scroll .c-name {
  position: sticky;
  z-index: 1;
}
.rv-card.is-scroll thead .c-place,
.rv-card.is-scroll thead .c-fav,
.rv-card.is-scroll thead .c-name {
  z-index: 3;
}
.rv-card.is-scroll .c-place {
  left: 0;
}
.rv-card.is-scroll .c-fav {
  left: var(--lv-fav-left, 76px);
}
.rv-card.is-scroll .c-name {
  left: var(--lv-name-left, 116px);
  max-width: 240px;
}
.rv-card.is-scroll .c-name .rv-name,
.rv-card.is-scroll .c-name .rv-bio {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rv-card.is-scrolled .c-name {
  box-shadow: 6px 0 8px -6px rgba(15, 23, 42, 0.25);
}

.lv-cell {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  padding: 3px 7px;
  border-radius: 8px;
}
.lv-cell__time {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--sledz-text);
}
.lv-cell__place {
  font-size: 12px;
  font-weight: 700;
  color: var(--sledz-text-muted);
}
/* ostatni punkt zawodnika w trasie: tło i ramka, nie sam kolor */
.lv-cell.is-last {
  background: var(--sledz-primary-soft);
  box-shadow: inset 0 0 0 1px var(--sledz-primary-soft);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--sledz-primary) 45%, var(--sledz-surface));
}
.lv-cell.is-last .lv-cell__time {
  font-weight: 800;
  color: var(--sledz-primary-deep);
}
.lv-cell--meta .lv-cell__time {
  font-weight: 600;
}
.lv-none {
  color: var(--sledz-border);
  font-weight: 800;
}
.lv-running {
  font-size: 15px;
}
.lv-star {
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.lv-star svg {
  fill: none;
  stroke: var(--sledz-text-muted);
}
/* na dotyk :hover zostaje po stuknięciu — tylko dla myszy */
@media (hover: hover) {
  .lv-star:hover {
    background: var(--sledz-surface-alt);
  }
}
.lv-star.is-on svg {
  fill: var(--sledz-brand);
  stroke: var(--sledz-warning-ink);
}
.rv-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--sledz-text);
  white-space: nowrap;
}
.rv-bio {
  display: block;
  margin-top: 1px;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  color: var(--sledz-text-muted);
}
.rv-bib {
  flex: none;
  display: inline-block;
  min-width: 34px;
  padding: 1px 5px;
  border-radius: 6px;
  background: var(--sledz-border-soft);
  color: var(--sledz-text-secondary);
  text-align: center;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.rv-time {
  display: block;
  font-size: 16px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--sledz-text);
}
.rv-sub {
  display: block;
  font-size: 12.5px;
  color: var(--sledz-text-muted);
  white-space: nowrap;
}
.rv-muted {
  color: var(--sledz-text-muted);
  font-size: 13.5px;
}
.rv-penalty {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--sledz-warning-ink);
  white-space: nowrap;
}
.rv-medal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 4px;
  border-radius: 15px;
  font-size: 15px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--sledz-text);
}
.rv-medal--1,
.rv-medal--2,
.rv-medal--3 {
  font-size: 14px;
  font-weight: 900;
  color: var(--sledz-brand-ink);
  box-shadow: inset 0 -2px 0 rgba(0, 0, 0, 0.12);
}
.rv-medal--1 {
  background: var(--sledz-brand);
}
.rv-medal--2 {
  background: var(--sledz-medal-silver);
}
.rv-medal--3 {
  background: var(--sledz-medal-bronze);
}
.rv-tag {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--sledz-border-soft);
  color: var(--sledz-text-secondary);
  font-size: 11.5px;
  font-weight: 800;
  white-space: nowrap;
}
.rv-tag--dnf {
  background: var(--sledz-surface-alt);
  background: color-mix(in srgb, var(--sledz-live) 12%, var(--sledz-surface));
  color: var(--sledz-live-strong);
}
.rv-dash {
  color: var(--sledz-text-muted);
}
.rv-gps {
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 4px;
  background: var(--sledz-warning-soft);
  color: var(--sledz-warning-ink);
  font-size: 10.5px;
  font-weight: 800;
  font-style: normal;
}
.rv-more {
  margin: 0;
  padding: 12px 16px;
  text-align: center;
  font-size: 13.5px;
  color: var(--sledz-text-muted);
  list-style: none;
}

/* ---------- phone ---------- */
.rv-ptools {
  position: sticky;
  top: 0;
  z-index: 5;
  margin: -20px -16px 0;
  padding: 10px 16px;
  background: var(--sledz-surface-alt);
  border-bottom: 1px solid var(--sledz-border-soft);
}
.rv-ptools__row {
  display: flex;
  gap: 8px;
}
.rv-search--phone {
  flex: 1;
  width: auto;
  min-width: 0;
  height: 44px;
}
.rv-search--phone input {
  font-size: 16px; /* mniejsza czcionka = iOS przybliża stronę */
}
.rv-filters-btn {
  flex: none;
  height: 44px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid var(--sledz-border);
  background: var(--sledz-surface);
  color: var(--sledz-text);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14.5px;
  font-weight: 700;
}
.rv-filters-btn.is-active {
  border-color: var(--sledz-primary);
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.rv-filters-btn__badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--sledz-primary);
  color: var(--sledz-surface);
  font-size: 11.5px;
  line-height: 18px;
  text-align: center;
}
.rv-chips {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.rv-chip {
  height: 32px;
  padding: 0 4px 0 12px;
  border-radius: 16px;
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13.5px;
  font-weight: 700;
}
.rv-chip button {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--sledz-primary-deep);
  font-size: 18px;
  line-height: 1;
}
.rv-plist {
  margin: 0 -16px;
  padding: 0;
  list-style: none;
  background: var(--sledz-surface);
}
.rv-plist__section h2 {
  margin: 0;
  padding: 16px 16px 6px;
  background: var(--sledz-surface-alt);
  border-bottom: 1px solid var(--sledz-border-soft);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
}
.lv-pitem {
  border-bottom: 1px solid var(--sledz-border-soft);
  content-visibility: auto;
  contain-intrinsic-size: auto 76px;
}
.lv-pitem.is-changed .lv-prow {
  animation: rv-flash 1.2s ease-out;
  background: var(--sledz-highlight);
}
.lv-pitem.is-open {
  background: var(--sledz-surface-alt);
  background: color-mix(in srgb, var(--sledz-primary-soft) 45%, var(--sledz-surface));
}
.lv-prow {
  display: flex;
  align-items: center;
}
.lv-prow__btn {
  flex: 1;
  min-width: 0;
  min-height: 72px;
  padding: 8px 4px 8px 16px;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;
}
.rv-prow__place {
  flex: none;
  width: 30px;
  display: flex;
  justify-content: center;
}
.rv-prow__main {
  flex: 1;
  min-width: 0;
}
.rv-prow__name {
  display: block;
  font-size: 15.5px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rv-prow__sub {
  display: block;
  margin-top: 2px;
  font-size: 13px;
  color: var(--sledz-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rv-prow__right {
  flex: none;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.rv-prow__right .rv-sub {
  text-align: right;
}
.lv-star--phone {
  flex: none;
  width: 44px;
  height: 44px;
  margin-right: 4px;
}
.lv-dots {
  display: flex;
  align-items: center;
  max-width: 100%;
  margin-top: 7px;
}
.lv-dots__dot {
  flex: none;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--sledz-surface);
  border: 2px solid var(--sledz-border);
}
.lv-dots__dot.is-done {
  background: var(--sledz-primary);
  border-color: var(--sledz-primary);
}
.lv-dots__line {
  flex: 1;
  height: 2px;
  background: var(--sledz-border-soft);
}
.lv-dots__line.is-done {
  background: var(--sledz-primary);
}
.lv-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 7px;
}
.lv-bar__track {
  flex: 1;
  max-width: 180px;
  height: 6px;
  border-radius: 3px;
  background: var(--sledz-border-soft);
  overflow: hidden;
}
.lv-bar__fill {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: var(--sledz-primary);
}
.lv-bar__text {
  font-size: 12px;
  font-weight: 700;
  color: var(--sledz-text-muted);
  font-variant-numeric: tabular-nums;
}

/* oś punktów (L3) */
.lv-axis {
  padding: 2px 16px 14px 56px;
}
.lv-axis__list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.lv-axis__item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 6px 0 10px;
}
.lv-axis__item::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 18px;
  bottom: -10px;
  width: 2px;
  background: var(--sledz-border-soft);
}
.lv-axis__item.is-line-done::before {
  background: var(--sledz-primary);
}
.lv-axis__item.is-end::before {
  display: none;
}
.lv-axis__node {
  position: relative;
  z-index: 1;
  flex: none;
  margin-top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--sledz-surface);
  border: 2px solid var(--sledz-border);
}
.lv-axis__item.is-done .lv-axis__node {
  background: var(--sledz-primary);
  border-color: var(--sledz-primary);
}
.lv-axis__item.is-current .lv-axis__node {
  box-shadow: 0 0 0 4px var(--sledz-primary-soft);
}
.lv-axis__label {
  flex: 1;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--sledz-text-muted);
}
.lv-axis__item.is-done .lv-axis__label {
  color: var(--sledz-text);
}
.lv-axis__item.is-current .lv-axis__label {
  font-weight: 800;
}
.lv-axis__km {
  font-weight: 400;
  color: var(--sledz-text-muted);
}
.lv-axis__value {
  flex: none;
  font-size: 14.5px;
  font-variant-numeric: tabular-nums;
  color: var(--sledz-text);
}
.lv-axis__place {
  color: var(--sledz-text-muted);
}
.lv-axis__onway {
  margin: -2px 0 10px 24px;
  padding: 6px 10px;
  border-radius: 8px;
  background: var(--sledz-surface);
  border: 1px dashed var(--sledz-primary);
  font-size: 13px;
  color: var(--sledz-primary-deep);
}
.lv-axis__onway :deep(.running-time) {
  font-style: normal;
  color: var(--sledz-primary-deep);
}
.lv-axis__actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.lv-btn {
  flex: 1;
  height: 40px;
  border-radius: 20px;
  border: 1px solid var(--sledz-border);
  background: var(--sledz-surface);
  color: var(--sledz-text);
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 800;
}
.lv-btn--primary {
  border-color: var(--sledz-primary);
  color: var(--sledz-primary-deep);
}

/* ---------- filter sheet ---------- */
.rv-sheet {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: flex-end;
}
.rv-sheet__panel {
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 20px 16px calc(20px + env(safe-area-inset-bottom));
  border-radius: 18px 18px 0 0;
  background: var(--sledz-surface);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
}
.rv-sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.rv-sheet__head h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 800;
}
.rv-sheet h3 {
  margin: 18px 0 8px;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
}
.rv-seg--wide {
  display: flex;
}
.rv-seg--wide button {
  flex: 1;
  height: 38px;
  padding: 0 8px;
}
.rv-sheet__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.rv-pick {
  height: 38px;
  padding: 0 14px;
  border-radius: 19px;
  border: 1px solid var(--sledz-border);
  background: var(--sledz-surface);
  color: var(--sledz-text);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.rv-pick[aria-pressed='true'] {
  border: 1.5px solid var(--sledz-primary);
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.rv-link {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--sledz-primary-deep);
}
.rv-sheet__go {
  margin-top: 22px;
  width: 100%;
  height: 50px;
  border: none;
  border-radius: 12px;
  background: var(--sledz-primary);
  color: var(--sledz-surface);
  font-size: 16px;
  font-weight: 800;
}

@media (max-width: 767.98px) {
  .rv__main {
    padding: 20px 16px 0;
    gap: 0;
  }
  .lv-gpsnote {
    margin: 0 -16px;
    border-radius: 0;
    border-width: 0 0 1px;
    font-size: 13px;
  }
  .rv-note {
    margin: 0 -16px;
    padding: 8px 16px;
    background: var(--sledz-surface);
    border-bottom: 1px solid var(--sledz-border-soft);
  }
}
</style>
